import { array, fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import moment from 'moment';

import type { DSSetup, PopupListItem, RadiobuttonItemInfo, Service } from 'webapp-types';
import type { BlockVisualization, Theme } from 'webapp/enums/enums';
import {
	AttachPosition,
	DateRangeTypeId,
	GranulationTypeId,
	PopupEvent,
	TableColumnType,
	TimeUnit,
} from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { getFormattedPeriod } from 'webapp/utils/number';

import eq from 'webapp/helpers/eq';
import joinClasses from 'webapp/helpers/join-classes';
import themeClasses from 'webapp/helpers/theme-classes';
import BlockMasterFilterComponent from 'webapp/components/block/master-filter';
import BlockSettingsVisualizationsDropdownComponent from 'webapp/components/block/settings/visualizations-dropdown';
import CommonPopupsPopupListComponent from 'webapp/components/common/popups/popup-list';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormCumulativeSwitchComponent from 'webapp/components/form/cumulative-switch';
import FormGranulationDropdownComponent from 'webapp/components/form/granulation/dropdown';
import FormRadiobuttonGroupComponent from 'webapp/components/form/radiobutton-group';
import FormRangeDropdownComponent from 'webapp/components/form/range/dropdown';
import FormSelectCompareTypeComponent from 'webapp/components/form/select-compare-type';
import ToolTipComponent from 'webapp/components/tool-tip';

import type { BlockState } from 'webapp/models-custom';
import type DataSelect from 'webapp/models-custom/data-select';
import DateRange from 'webapp/models-custom/date-range';
import type DsCompare from 'webapp/models-custom/ds-compare';
import PopupList from 'webapp/models-custom/popup/popup-list';

interface Args {
	blockState: BlockState;
	setModelIsDirty?: () => void;
	theme?: Theme;
	showCumulative: boolean;
	showMasterFilter: boolean;
	showCompare: boolean;
	showVisualizationsAsGrid?: boolean;
	origin?: string; // "datablock_explorer" (default) | "metrics_detail"
}

// a date range option (button, period picker, XTD dropdown and its items) with its date range resolved
interface DateRangeOption {
	label: string;
	value: string;
	dateRange?: DateRange | null;
	isLocked: boolean;
	isButton?: boolean;
	isDropdown?: boolean;
}

export default class BlockSettingsPreviewTopbarComponent extends Component<Args> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('store') declare storeService: Service.Store;
	@service('model-manager/block-previews')
	declare mmBlockPreviewsService: Service.ModelManager.BlockPreviews;

	@tracked selectedOption: DateRangeOption | null = null;
	@tracked selectedXTDOption: DateRangeOption | null = null;
	@tracked isCustomSelected = true;
	@tracked isDropdownOpen = false;

	@tracked rangeStartDate: string | null = null;
	@tracked rangeEndDate: string | null = null;

	private xtdPopup: PopupList | null = null;

	get origin(): string {
		return this.args.origin ?? 'datablock_explorer';
	}

	options = [
		{
			label: 'Custom',
			value: 'custom',
			rangeTypeId: DateRangeTypeId.CUSTOM,
			isPeriodPicker: true,
		},
		{
			label: '7D',
			rangeTypeId: DateRangeTypeId.LAST_X_DAYS,
			rangeTimeUnit: TimeUnit.DAY,
			rangeMultiplier: 7,
			isButton: true,
		},
		{
			label: '30D',
			rangeTypeId: DateRangeTypeId.LAST_X_DAYS,
			rangeTimeUnit: TimeUnit.DAY,
			rangeMultiplier: 30,
			isButton: true,
		},
		{
			label: '3M',
			rangeTypeId: DateRangeTypeId.LAST_X_MONTHS,
			rangeTimeUnit: TimeUnit.MONTH,
			rangeMultiplier: 3,
			isButton: true,
		},
		{
			label: '6M',
			rangeTypeId: DateRangeTypeId.LAST_X_MONTHS,
			rangeTimeUnit: TimeUnit.MONTH,
			rangeMultiplier: 6,
			isButton: true,
		},
		{
			label: '12M',
			rangeTypeId: DateRangeTypeId.LAST_X_MONTHS,
			rangeTimeUnit: TimeUnit.MONTH,
			rangeMultiplier: 12,
			isButton: true,
		},
		{
			label: 'XTD', // "X" will be replaced with time unit string in optionsWithDateRanges
			rangeTypeId: 'XToDate' as DateRangeTypeId,
			isDropdown: true,
		},
	];

	xtdOptions = [
		{
			label: 'WTD',
			rangeTypeId: DateRangeTypeId.WEEK_TO_DATE,
			dropdownLabel: 'Week to Date',
		},
		{
			label: 'MTD',
			rangeTypeId: DateRangeTypeId.MONTH_TO_DATE,
			dropdownLabel: 'Month to Date',
		},
		{
			label: 'QTD',
			rangeTypeId: DateRangeTypeId.QUARTER_TO_DATE,
			dropdownLabel: 'Quarter to Date',
		},
		{
			label: 'YTD',
			rangeTypeId: DateRangeTypeId.YEAR_TO_DATE,
			dropdownLabel: 'Year to Date',
		},
	];

	get optionsWithDateRanges() {
		return this.options.map((option) => {
			let value = option.rangeTypeId as string;
			let dateRange = null;
			let tooltipText = '';

			if (option.isButton) {
				if (option.rangeMultiplier && option.rangeTimeUnit) {
					dateRange = DateRange.createLastX(
						option.rangeMultiplier,
						option.rangeTimeUnit,
						GranulationTypeId.TOTAL,
					);

					value = value.replace('X', String(option.rangeMultiplier));
					tooltipText = this.getFormattedDateRangeForTooltip(dateRange);
				}
			} else if (option.isPeriodPicker) {
				dateRange = this.metricLimitations?.ranges
					.find((item) => item.rangeTypeId === option.rangeTypeId)
					?.createCopy();
			}

			return {
				...option,
				value,
				dateRange,
				tooltipText,
				isLocked: !!dateRange && !this.metricLimitations.isDateRangeSupported(dateRange),
			};
		});
	}

	get optionsWithXTDDateRanges() {
		return this.xtdOptions.map((option) => {
			const _dateRange = this.metricLimitations.ranges.find(
				(item) => item.rangeTypeId === option.rangeTypeId,
			);

			let dateRange = null;
			if (_dateRange) {
				dateRange = _dateRange.createCopy();
			}

			return {
				...option,
				value: option.rangeTypeId,
				dateRange,
				isLocked: !!dateRange && !this.metricLimitations.isDateRangeSupported(dateRange),
			};
		});
	}

	get model() {
		return this.args.blockState.block;
	}

	get blockSetup() {
		return this.model.blockSetup;
	}

	get visualization() {
		return this.blockSetup.visualization;
	}

	get metricLimitations() {
		return this.args.blockState.limitationsCM!;
	}

	get attachPosition() {
		return AttachPosition.START;
	}

	get showMasterFilter(): boolean {
		return this.args.showMasterFilter;
	}

	get showCumulative(): boolean {
		return this.args.showCumulative && !!this.visualization.showCumulative;
	}

	get showCompare(): boolean {
		return this.args.showCompare && !!this.blockSetup.firstShownDataSelect?.limitations.compare;
	}

	get visualizationOptions(): RadiobuttonItemInfo[] {
		return this.model.switchableVisualizationList.map((viz) => ({
			value: viz.id,
			label: '', // icon-only
			icon: viz.icon,
			tooltipText: viz.label, // hover label, since the text is hidden
		}));
	}

	// guarded in template
	get primaryDataSelect(): DataSelect {
		return this.blockSetup.firstShownDataSelect!;
	}

	// guarded in template
	get currentCompare(): DsCompare {
		return this.primaryDataSelect.limitations.compare!;
	}

	selectOption(option: DateRangeOption) {
		this.isCustomSelected = false;
		this.selectedOption = option;
		this.selectedXTDOption = null;

		this.changeDateRange(option.dateRange!); // set on the buttons
	}

	selectXTDOption(option: DateRangeOption) {
		this.isCustomSelected = false;
		this.selectedOption = null;
		this.selectedXTDOption = option;

		this.changeDateRange(option.dateRange!); // set when the metric supports the range
	}

	@action
	performAction(option: DateRangeOption, e: PointerEvent) {
		if (option.isLocked) {
			return;
		}

		e.stopPropagation();
		e.preventDefault();
		if (option.isDropdown) {
			if (this.isDropdownOpen) {
				void this.xtdPopup?.close();
			} else {
				this.isDropdownOpen = true;
				this.openXTDOptions(e);
			}
		} else if (option.isButton) {
			this.isDropdownOpen = false;
			this.selectOption(option);
		} else {
			const target = e.target as HTMLElement;
			// when clicking the select2 trigger itself, let select2 toggle on its own
			if (target.closest('.select2')) {
				return;
			}
			$(target).closest('.item--period-picker').find('select').select2('open');
		}
	}

	@action
	openXTDOptions(e: PointerEvent) {
		if (this.popupService.isOpen(CommonPopupsPopupListComponent)) {
			return;
		}

		e.stopPropagation(); // don't toggle the section

		const itemElement = (e.target as HTMLElement).closest<HTMLElement>('.item');
		if (!itemElement) {
			return;
		}

		const popup = new PopupList({
			attachTo: itemElement,
			theme: this.args.theme,
			popupClass: 'optionspopup',
			attachPosition: AttachPosition.START,
			alignEdgeWithAttachTo: true,
			hideArrow: true,
		});
		this.xtdPopup = popup;

		const itemsList: PopupListItem<''>[] =
			this.optionsWithXTDDateRanges.map((option) => ({
				id: option.value,
				text: option.dateRange?.rangeTitleUX ?? '',
				action: '', // operating with id
				isSelected: this.selectedXTDOption?.value === option.value,
				isDisabled: option.isLocked,
				extra: option.isLocked
					? {
							type: 'featureLimitation',
						}
					: undefined,
			})) || [];

		this.analyticsService.event(TrackingEvent.DATE_RANGE_DROPDOWN_OPENED, {
			origin: this.origin,
		});

		popup.open({
			itemsList,
			doAction: (_actionName: string, id: string) => {
				const option = this.optionsWithXTDDateRanges.find(
					(item) => item.value === (id as DateRangeTypeId),
				);

				if (option) {
					this.selectXTDOption(option);
				}
			},
		});

		popup.on(PopupEvent.CLOSING, this, () => {
			this.isDropdownOpen = false;
			this.xtdPopup = null;
		});
	}

	@action
	changeCustomPeriod(dateRange: DateRange) {
		this.isCustomSelected = true;
		this.selectedOption = null;
		this.selectedXTDOption = null;

		this.changeDateRange(dateRange);
	}

	@action
	changeDateRange(dateRange: DateRange) {
		this.blockSetup.changeDateRange(dateRange);
		this.args.setModelIsDirty?.();
		this.blockSetup.trackMetricDataEvent(
			TrackingEvent.DATE_RANGE_CHANGED,
			undefined,
			this.origin,
		);
	}

	@action
	changeDimensionsFilter(
		dimensions?: string[] | null,
		filters?: DSSetup.MetricSetupFiltersData | null,
	) {
		const block = this.model;

		// extra change for table blocks when switching master dimensions (tableColumnType)
		// for more info, see mmBlockPreviewsService.getTableBlocksThatMustSwitchToAutoOnMasterDimensionsChange
		const mustUpdateTableColumnType =
			this.mmBlockPreviewsService.getTableBlocksThatMustSwitchToAutoOnMasterDimensionsChange(
				[block],
				dimensions || null,
			).length > 0;

		void this.mmBlocksService
			.updateBlockMasterMetricSetup(block, {
				dimensions: dimensions || undefined,
				filters: filters || null,
			})
			.then(() => {
				if (mustUpdateTableColumnType) {
					block.tableColumnType = TableColumnType.AUTO;
					void block.saveAndCatch();
				}
			});

		// To trigger changes and show blue badge
		this.blockSetup.block.changes = {
			metricSetup: {
				dimensions,
				filters,
			},
		};

		this.args.setModelIsDirty?.();
	}

	@action
	changeCumulative(cumulative: boolean): void {
		this.blockSetup.changeCumulative(cumulative);
		this.blockSetup.trackBlockPropertySettingsChangeEvent({ cumulative });
		this.args.setModelIsDirty?.();
	}

	@action
	changeCompare(newCompare: DsCompare): void {
		this.blockSetup.changeCompare(this.primaryDataSelect, this.currentCompare, newCompare);
		this.args.setModelIsDirty?.();
	}

	@action
	changeGranulation(granulationId: string | null): void {
		this.blockSetup.changeGranulation(granulationId as GranulationTypeId);
		this.args.setModelIsDirty?.();
		this.blockSetup.trackMetricDataEvent(
			TrackingEvent.GRANULATION_CHANGED,
			undefined,
			this.origin,
		);
	}

	@action
	changeVisualization(toVisualizationType: BlockVisualization): boolean {
		void this.blockSetup.changeVisualization(toVisualizationType);
		this.args.setModelIsDirty?.();
		this.analyticsService.event(TrackingEvent.BLOCK_VISUALIZATION_CHANGED, {
			origin: this.origin,
			visualization:
				this.mmBlocksService.getVisualizationNameFromVisualizationType(toVisualizationType),
		});

		return true;
	}

	@action
	onVisualizationChange(value: AnySimpleValue): void {
		this.changeVisualization(value as BlockVisualization);
	}

	// this logic can only be used here for predefined set of ranges used in block explorer!
	// this data is otherwise provided by backend
	private getFormattedDateRangeForTooltip(dateRange: DateRange): string {
		if (dateRange.isLastX) {
			const multiplier = dateRange.rangeMultiplier || 1,
				timeUnit = dateRange.rangeTimeUnit;

			if (!timeUnit) {
				return dateRange.rangeTitleUX || '';
			}

			const currentDate = moment(),
				endDate = moment(currentDate),
				startDate = moment(currentDate);

			switch (timeUnit) {
				case TimeUnit.DAY:
					// for days, exclude today - end date should be yesterday
					endDate.subtract(1, 'day');
					startDate.subtract(multiplier, 'days');
					break;
				case TimeUnit.MONTH:
					// for months, only use completed months
					// if today is 26.6., we want 1.3. to 31.5.
					endDate.subtract(1, 'month').endOf('month');
					startDate.subtract(multiplier, 'months').startOf('month');
					break;
				default:
					// default to days behavior
					endDate.subtract(1, 'day');
					startDate.subtract(multiplier, 'days').subtract(1, 'day');
			}

			return getFormattedPeriod(
				startDate.unix(),
				endDate.unix(),
				null,
				'D MMM, YYYY',
				'D MMM',
			);
		}

		// for other types, fall back to the descriptive title
		return dateRange.rangeTitleUX || '';
	}
	<template>
		<div class="block-settings-bar {{themeClasses @theme}}">
			<div class="block-settings-bar__left">
				<div class="daterange">
					{{#each this.optionsWithDateRanges as |item|}}
						<div
							role="button"
							class={{joinClasses
								(array
									"item"
									(if item.isLocked "item--locked")
									(if item.isPeriodPicker "item--period-picker")
									(if item.isDropdown "item--dropdown")
									(if this.isDropdownOpen "item--open")
									(if
										item.isDropdown (if this.selectedXTDOption "item--selected")
									)
									(if
										item.isButton
										(if
											(eq item.value this.selectedOption.value)
											"item--selected"
										)
									)
									(if
										item.isPeriodPicker
										(if this.isCustomSelected "item--selected" "")
									)
								)
							}}
							{{on "click" (fn this.performAction item)}}
						>
							{{#if item.isPeriodPicker}}
								<div
									class={{joinClasses
										(array
											"range-dropdown-container"
											(unless
												this.isCustomSelected
												"range-dropdown-container--hidden"
											)
										)
									}}
								>
									<FormRangeDropdownComponent
										@metricLimitations={{this.metricLimitations}}
										@onChange={{this.changeCustomPeriod}}
										@isDisabled={{item.isLocked}}
										@theme={{@theme}}
										@customPeriodPickerOptions={{hash placement="bottom"}}
										@popupOptions={{hash
											skipAttachToCheck=true
											offset=6
											attachPosition=this.attachPosition
											alignEdgeWithAttachTo=true
										}}
									/>
								</div>
								<span>
									<CommonUiIconComponent @name="Calendar" @size="md" />
									Custom
								</span>
							{{else if item.isDropdown}}
								<span class="arrow">
									{{#if this.selectedXTDOption}}
										{{this.selectedXTDOption.label}}
									{{else}}
										{{item.label}}
									{{/if}}
									<CommonUiIconComponent
										@name="ChevronDown"
										@size="xs"
										@class="arrow-icon"
									/>
								</span>
							{{else}}
								{{item.label}}
							{{/if}}
							{{#if item.isLocked}}
								<CommonUiIconComponent @name="Lock" @size="sm" />
							{{/if}}
							{{#if item.tooltipText}}
								<ToolTipComponent
									@text={{item.tooltipText}}
									@theme={{@theme}}
									@maxWidth={{200}}
								/>
							{{/if}}
						</div>
					{{/each}}
				</div>
				{{#if this.showMasterFilter}}
					<BlockMasterFilterComponent
						@blockState={{@blockState}}
						@save={{this.changeDimensionsFilter}}
						@theme={{@theme}}
						@trackingOrigin={{this.origin}}
					/>
				{{/if}}
			</div>

			<div class="block-settings-bar__right">

				{{#if this.showCumulative}}
					<div class="cumulative-switch">
						<FormCumulativeSwitchComponent
							@metricLimitations={{this.metricLimitations}}
							@onChange={{this.changeCumulative}}
							@theme={{@theme}}
						/>
					</div>
				{{/if}}

				{{#if this.visualization.showGranulation}}
					<div class="granulation-dropdown">
						<FormGranulationDropdownComponent
							@metricLimitations={{this.metricLimitations}}
							@onChange={{this.changeGranulation}}
							@placeholder="Graph by"
							@theme={{@theme}}
							@trackingOrigin={{this.origin}}
							@popupOffset={{1}}
						/>
					</div>
				{{/if}}

				{{#if this.showCompare}}
					<div class="compare-dropdown">
						<FormSelectCompareTypeComponent
							@model={{this.currentCompare}}
							@dataSelect={{this.primaryDataSelect}}
							@onChange={{this.changeCompare}}
							@placeholder="Compare to"
							@theme={{@theme}}
							@origin={{this.origin}}
						/>
					</div>
				{{/if}}

				{{#if @showVisualizationsAsGrid}}
					<FormRadiobuttonGroupComponent
						class="radiobutton-switcher radiobutton-switcher--icons-only"
						@theme="alternate"
						@name="visualization"
						@selected={{this.blockSetup.visualizationType}}
						@options={{this.visualizationOptions}}
						@onChange={{this.onVisualizationChange}}
					/>
				{{else}}
					<div class="visualizations-dropdown">
						<BlockSettingsVisualizationsDropdownComponent
							@blockM={{this.model}}
							@onChange={{this.changeVisualization}}
							@theme={{@theme}}
							@extraClass="topbar-select"
						/>
					</div>
				{{/if}}
			</div>
		</div>
	</template>
}
